import React, { useState } from "react";

import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";

import { formatEmployeeProfileAsInitialValues } from "Common/EnterAges/Components/Helpers/FormHelpers";

import QuoteEmployeePriceBreakdownForm from "./QuoteEmployeePriceBreakdownForm";

const focusOnErrors = createDecorator();

export const applyCostsToForm = ({ formEmployeeProfiles, employeeCosts, contributions, pricingType }) =>
  formEmployeeProfiles.map((employee, i) => {
    const eeCosts = employeeCosts[i];
    let premium;
    if (eeCosts) {
      premium = eeCosts.self_cost;
    } else {
      premium = 0;
    }

    let companyCost = 0;
    if (contributions.percentage) {
      companyCost = Math.min(premium, Math.max(0, premium * (contributions.percentage.employee / 100)));
    } else if (contributions.fixed) {
      companyCost = Math.min(premium, Math.max(0, parseInt(contributions.fixed.employee, 10)));
    }
    const employeeCost = premium - companyCost;
    employee = {
      ...employee,
      companyCost: companyCost ? companyCost.toFixed(2) : null,
      companyCostInt: companyCost || 0,
      employeeCost: employeeCost ? employeeCost.toFixed(2) : null,
      employeeCostInt: employeeCost || 0,
      premium: premium ? premium.toFixed(2) : null,
      premiumInt: premium || 0,
    };

    if (contributions.percentage && pricingType != "segment") {
      employee.dependents = employee.dependents.map((dependent) => {
        let premium = 0;
        if (eeCosts) {
          if (dependent.type == "Spouse") {
            premium = eeCosts.spouse_cost;
          } else {
            premium = (eeCosts.child_cost || [])[dependent.childIndex];
          }
        }

        let companyCost = 0;
        if (contributions.percentage) {
          companyCost = Math.min(premium, Math.max(0, premium * (contributions.percentage.dependent / 100)));
        } else if (contributions.fixed) {
          companyCost = Math.min(premium, Math.max(0, parseInt(contributions.fixed.dependent, 10)));
        }
        const dependentCost = premium - companyCost;

        return {
          ...dependent,
          companyCost: companyCost ? companyCost.toFixed(2) : null,
          companyCostInt: companyCost || 0,
          employeeCost: dependentCost ? dependentCost.toFixed(2) : null,
          employeeCostInt: dependentCost || 0,
          premium: premium ? premium.toFixed(2) : null,
          premiumInt: premium || 0,
        };
      });
    } else if (contributions.fixed || pricingType == "segment") {
      let premium = 0;
      let companyCost = 0;
      if (eeCosts) {
        premium = eeCosts.family_cost;
      }

      if (contributions.percentage) {
        companyCost = Math.min(premium, Math.max(0, premium * (contributions.percentage.dependent / 100)));
      } else if (contributions.fixed) {
        companyCost = Math.min(premium, Math.max(0, parseInt(contributions.fixed.dependent, 10)));
      }
      const dependentCost = premium - companyCost;

      employee.dependentTotals = {
        premium: premium.toFixed(2),
        premiumInt: premium,
        companyCost: companyCost.toFixed(2),
        companyCostInt: companyCost,
        employeeCost: dependentCost.toFixed(2),
        employeeCostInt: dependentCost,
      };
    }

    return employee;
  });

export const calculateTotals = (employees) => {
  let totalCompanyCost = 0.0;
  let totalEmployeeCost = 0.0;
  let totalCost = 0.0;

  employees.forEach((ee) => {
    totalCompanyCost += ee.companyCostInt;
    totalEmployeeCost += ee.employeeCostInt;
    totalCost += ee.premiumInt;
    if (ee.dependentTotals) {
      totalCompanyCost += ee.dependentTotals.companyCostInt;
      totalEmployeeCost += ee.dependentTotals.employeeCostInt;
      totalCost += ee.dependentTotals.premiumInt;
    } else {
      ee.dependents?.forEach((dependent) => {
        totalCompanyCost += dependent.companyCostInt;
        totalEmployeeCost += dependent.employeeCostInt;
        totalCost += dependent.premiumInt;
      });
    }
  });

  return { totalCost, totalEmployeeCost, totalCompanyCost };
};

export const QuoteEmployeePriceBreakdownBody = ({ plan: medicalPlan }) => {
  const employeeProfiles = useSelector((state) => state.employeeProfile);

  const [contributionType, setContributionType] = useState("percentage");
  const [employeeContribution, setEmployeeContribution] = useState(50);
  const [dependentContribution, setDependentContribution] = useState(0);

  let contributions = {};

  if (contributionType == "percentage") {
    contributions = {
      percentage: {
        employee: employeeContribution,
        dependent: dependentContribution,
      },
    };
  } else {
    contributions = {
      fixed: {
        employee: employeeContribution,
        dependent: dependentContribution,
      },
    };
  }

  const employeeCosts = (medicalPlan && medicalPlan.employee_costs) || [];
  const formEmployeeProfiles = applyCostsToForm({
    formEmployeeProfiles: formatEmployeeProfileAsInitialValues(employeeProfiles),
    employeeCosts,
    contributions,
    pricingType: medicalPlan.pricing_type,
  });

  const initialValues = {
    employee_contribution: employeeContribution,
    dependent_contribution: dependentContribution,
    employees: formEmployeeProfiles,
  };

  const totals = calculateTotals(formEmployeeProfiles);
  return (
    <Form onSubmit={() => {}} decorators={[focusOnErrors]} initialValues={initialValues} mutators={{ ...arrayMutators }}>
      {(props) => (
        <QuoteEmployeePriceBreakdownForm
          {...props}
          medicalPlan={medicalPlan}
          formEmployeeProfiles={formEmployeeProfiles}
          totals={totals}
          contributionType={contributionType}
          setContributions={{
            setContributionType,
            setEmployeeContribution,
            setDependentContribution,
          }}
        />
      )}
    </Form>
  );
};

export default QuoteEmployeePriceBreakdownBody;
